import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { motion } from "motion/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import type { CreateSuperAdminTenantResponse } from "@victor/types";
import { api } from "../../lib/api";
import { Card } from "../../ui/Card";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { Modal } from "../../ui/Modal";
import { EmptyState } from "../../ui/EmptyState";
import { PageSpinner } from "../../ui/Spinner";

const STATUTS = [
  { value: "all", label: "Tous statuts" },
  { value: "actif", label: "Actives" },
  { value: "suspendu", label: "Suspendues" },
  { value: "resilie", label: "Résiliées" },
] as const;

const STATUT_META: Record<string, { label: string; cls: string; dot: string }> = {
  actif: { label: "Actif", cls: "bg-emerald-50 text-emerald-700 ring-emerald-100", dot: "bg-emerald-500" },
  pre_souscription: { label: "Pré-souscription", cls: "bg-accent-50 text-accent-700 ring-accent-100", dot: "bg-accent-500" },
  suspendu: { label: "Suspendu", cls: "bg-amber-50 text-amber-800 ring-amber-100", dot: "bg-amber-500" },
  resilie: { label: "Résilié", cls: "bg-navy-100 text-navy-500 ring-navy-200", dot: "bg-navy-300" },
};
function statutMeta(s: string) {
  return (
    STATUT_META[s] ?? {
      label: s,
      cls: "bg-navy-100 text-navy-500 ring-navy-200",
      dot: "bg-navy-300",
    }
  );
}

const PLAN_META: Record<string, string> = {
  pro: "bg-accent-50 text-accent-700 ring-accent-100",
  business: "bg-navy-100 text-navy-700 ring-navy-200",
  starter: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  trial: "bg-amber-50 text-amber-700 ring-amber-100",
  test: "bg-navy-50 text-navy-500 ring-navy-200",
  sur_mesure: "bg-accent-50 text-accent-700 ring-accent-100",
};
function planCls(plan: string) {
  return PLAN_META[plan.toLowerCase()] ?? "bg-navy-50 text-navy-500 ring-navy-200";
}

function initials(name: string): string {
  const parts = name.replace(/[[\]]/g, "").trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (a + b).toUpperCase();
}
const AVATAR_BG = [
  "bg-accent-100 text-accent-700",
  "bg-emerald-100 text-emerald-700",
  "bg-amber-100 text-amber-700",
  "bg-navy-100 text-navy-700",
];
function avatarCls(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % AVATAR_BG.length;
  return AVATAR_BG[h] ?? AVATAR_BG[0]!;
}

function formatBlockedReason(reason: string): string {
  if (reason === "payment_failed") return "Échec de paiement";
  if (reason.startsWith("manual:")) return reason.slice("manual:".length).trim() || "Bloqué manuellement";
  return reason;
}

const SUM_TONES = {
  navy: { tile: "bg-navy-50 text-navy-700", ring: "ring-navy-300" },
  emerald: { tile: "bg-emerald-50 text-emerald-600", ring: "ring-emerald-300" },
  amber: { tile: "bg-amber-50 text-amber-600", ring: "ring-amber-300" },
  slate: { tile: "bg-navy-100 text-navy-500", ring: "ring-navy-300" },
} as const;

function SummaryTile({
  label,
  value,
  icon,
  tone,
  active,
  onClick,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  tone: keyof typeof SUM_TONES;
  active: boolean;
  onClick: () => void;
}) {
  const t = SUM_TONES[tone];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-3 rounded-2xl bg-white p-4 text-left ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        active ? `${t.ring} shadow-sm` : "ring-navy-100"
      }`}
    >
      <span
        className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${t.tile} [&>svg]:h-5 [&>svg]:w-5`}
      >
        {icon}
      </span>
      <div>
        <p className="text-2xl font-black leading-none tracking-tight text-navy-800">
          {value}
        </p>
        <p className="mt-1 text-xs font-semibold text-navy-500">{label}</p>
      </div>
    </button>
  );
}

const sumSp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const SUM_ICON = {
  building: (
    <svg {...sumSp}><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg>
  ),
  check: (
    <svg {...sumSp}><path d="M20 6 9 17l-5-5" /></svg>
  ),
  pause: (
    <svg {...sumSp}><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
  ),
  x: (
    <svg {...sumSp}><path d="M18 6 6 18M6 6l12 12" /></svg>
  ),
};

export function TenantsPage() {
  const navigate = useNavigate();
  const [statutFilter, setStatutFilter] = useState<string>("all");
  const [q, setQ] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] =
    useState<CreateSuperAdminTenantResponse | null>(null);

  const tenantsQ = useQuery({
    queryKey: ["tenants", statutFilter, q],
    queryFn: () =>
      api.tenants.list({
        statut: statutFilter === "all" ? undefined : statutFilter,
        q: q || undefined,
      }),
  });
  const statsQ = useQuery({ queryKey: ["stats"], queryFn: () => api.stats.get() });
  const tn = statsQ.data?.tenants;

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap items-end justify-between gap-3"
      >
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-navy-800 sm:text-4xl">
            Agences clientes
          </h1>
          <p className="mt-2 text-navy-600">
            Création, suspension et suivi des tenants Victor.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>+ Nouvelle agence</Button>
      </motion.header>

      {/* Bandeau de synthèse — cliquable, pilote le filtre */}
      {tn ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryTile
            label="Total agences"
            value={tn.total}
            icon={SUM_ICON.building}
            tone="navy"
            active={statutFilter === "all"}
            onClick={() => setStatutFilter("all")}
          />
          <SummaryTile
            label="Actives"
            value={tn.actifs}
            icon={SUM_ICON.check}
            tone="emerald"
            active={statutFilter === "actif"}
            onClick={() => setStatutFilter("actif")}
          />
          <SummaryTile
            label="Suspendues"
            value={tn.suspendus}
            icon={SUM_ICON.pause}
            tone="amber"
            active={statutFilter === "suspendu"}
            onClick={() => setStatutFilter("suspendu")}
          />
          <SummaryTile
            label="Résiliées"
            value={tn.resilies}
            icon={SUM_ICON.x}
            tone="slate"
            active={statutFilter === "resilie"}
            onClick={() => setStatutFilter("resilie")}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Filtre statut segmenté */}
        <div className="inline-flex rounded-xl bg-navy-50 p-1 ring-1 ring-navy-100">
          {STATUTS.map((s) => {
            const active = statutFilter === s.value;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatutFilter(s.value)}
                className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                  active
                    ? "bg-white text-navy-800 shadow-sm ring-1 ring-navy-100"
                    : "text-navy-500 hover:text-navy-800"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
        {/* Recherche */}
        <div className="relative w-full sm:w-72">
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-navy-400"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une agence…"
            className="w-full rounded-xl border border-navy-200 bg-white py-2.5 pl-9 pr-3 text-sm text-navy-800 placeholder:text-navy-400 transition-colors focus:border-accent-500 focus:outline-none focus:ring-4 focus:ring-accent-500/10"
          />
        </div>
      </div>

      {tenantsQ.data ? (
        <p className="text-sm text-navy-500">
          <span className="font-bold text-navy-800">{tenantsQ.data.length}</span>{" "}
          agence{tenantsQ.data.length > 1 ? "s" : ""}
          {statutFilter !== "all"
            ? ` · ${STATUTS.find((s) => s.value === statutFilter)?.label.toLowerCase()}`
            : ""}
        </p>
      ) : null}

      {tenantsQ.isLoading ? <PageSpinner /> : null}

      {tenantsQ.data && tenantsQ.data.length === 0 ? (
        <EmptyState
          title="Aucune agence"
          desc="Créez votre première agence cliente Victor."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              + Nouvelle agence
            </Button>
          }
        />
      ) : null}

      {tenantsQ.data && tenantsQ.data.length > 0 ? (
        <Card padded={false} className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b border-navy-100 bg-navy-50/50 text-navy-500">
              <tr>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider">
                  Agence
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider">
                  Users
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider">
                  Sites
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider">
                  Agents
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wider">
                  Factures
                </th>
                <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {tenantsQ.data.map((t) => (
                <tr
                  key={t.id}
                  className="group cursor-pointer transition-colors hover:bg-accent-50/40"
                  onClick={() => navigate(`/tenants/${t.id}`)}
                >
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-black ${avatarCls(t.raisonSociale)}`}
                      >
                        {initials(t.raisonSociale)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-navy-800">
                          {t.raisonSociale}
                        </p>
                        <p className="text-[11px] text-navy-400">
                          depuis{" "}
                          {new Date(t.createdAt).toLocaleDateString("fr-FR")}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ring-1 ${planCls(t.plan)}`}
                    >
                      {t.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums text-navy-700">
                    {t.utilisateursCount}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums text-navy-700">
                    {t.sitesCount}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums text-navy-700">
                    {t.agentsCount}
                  </td>
                  <td className="px-4 py-3.5 text-right tabular-nums text-navy-700">
                    {t.facturesEmisesCount}
                  </td>
                  <td className="px-5 py-3.5">
                    {(() => {
                      const m = statutMeta(t.statut);
                      return (
                        <div className="flex flex-col items-start gap-1">
                          <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${m.cls}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
                            {m.label}
                          </span>
                          {t.statut === "suspendu" && t.blockedReason ? (
                            <span className="text-[11px] text-amber-700">
                              {formatBlockedReason(t.blockedReason)}
                            </span>
                          ) : null}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-navy-400 transition-colors group-hover:text-accent-600">
                      Détails
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(r) => {
          setCreated(r);
          setCreateOpen(false);
        }}
      />
      <TenantCreatedModal result={created} onClose={() => setCreated(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create modal
// ---------------------------------------------------------------------------
function CreateTenantModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (r: CreateSuperAdminTenantResponse) => void;
}) {
  const queryClient = useQueryClient();
  const [raisonSociale, setRaisonSociale] = useState("");
  const [plan, setPlan] = useState<"trial" | "starter" | "pro" | "business">(
    "trial",
  );
  const [email, setEmail] = useState("");
  const [prenom, setPrenom] = useState("");
  const [nom, setNom] = useState("");

  const mut = useMutation({
    mutationFn: () =>
      api.tenants.create({
        raisonSociale,
        plan,
        gerant: { email, prenom, nom },
      }),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["tenants"] });
      void queryClient.invalidateQueries({ queryKey: ["stats"] });
      reset();
      onCreated(r);
    },
  });

  function reset() {
    setRaisonSociale("");
    setPlan("trial");
    setEmail("");
    setPrenom("");
    setNom("");
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    mut.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Nouvelle agence" size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Raison sociale"
          value={raisonSociale}
          onChange={(e) => setRaisonSociale(e.target.value)}
          required
          placeholder="Sécurité Dupont SAS"
        />
        <Select
          label="Plan initial"
          value={plan}
          onChange={(e) => setPlan(e.target.value as typeof plan)}
          options={[
            { value: "trial", label: "Trial" },
            { value: "starter", label: "Starter" },
            { value: "pro", label: "Pro" },
            { value: "business", label: "Business" },
          ]}
        />
        <div className="rounded-xl bg-navy-50/60 p-4 ring-1 ring-navy-100 space-y-3">
          <p className="text-xs font-bold uppercase tracking-wider text-navy-500">
            Gérant initial
          </p>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Prénom"
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              required
            />
            <Input
              label="Nom"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              required
            />
          </div>
          <p className="text-xs text-navy-500">
            Un mot de passe temporaire sera généré. Vous le transmettrez au
            gérant, qui devra immédiatement le changer via{" "}
            <em>/auth/forgot</em> ou la page Mon compte.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-navy-100">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? "Création…" : "Créer l'agence"}
          </Button>
        </div>

        {mut.error ? (
          <p
            role="alert"
            className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200"
          >
            {mut.error instanceof ApiClientError
              ? mut.error.message
              : "Erreur de création."}
          </p>
        ) : null}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Created modal (affiche le password temporaire UNE fois)
// ---------------------------------------------------------------------------
function TenantCreatedModal({
  result,
  onClose,
}: {
  result: CreateSuperAdminTenantResponse | null;
  onClose: () => void;
}) {
  if (!result) return null;
  const { tenant, gerant } = result;

  return (
    <Modal open={!!result} onClose={onClose} title="Agence créée" size="md">
      <div className="space-y-4">
        <div className="rounded-xl bg-emerald-50 px-4 py-3 ring-1 ring-emerald-200">
          <p className="text-sm font-bold text-emerald-800">
            ✓ {tenant.raisonSociale}
          </p>
          <p className="text-xs text-emerald-700">
            Plan : {tenant.plan} · gérant rattaché : {gerant.email}
          </p>
        </div>

        <div className="rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-amber-200 space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
            Mot de passe temporaire · à transmettre maintenant
          </p>
          <code className="block rounded-lg bg-white px-3 py-2 text-sm font-mono text-navy-800 ring-1 ring-amber-200 break-all">
            {gerant.temporaryPassword}
          </code>
          <p className="text-xs text-amber-700">
            Ne sera plus affiché après fermeture. Copiez-le et transmettez-le
            au gérant qui devra le changer immédiatement sur
            platform.myvictor.fr.
          </p>
          <Button
            variant="secondary"
            onClick={() =>
              void navigator.clipboard.writeText(gerant.temporaryPassword)
            }
          >
            Copier
          </Button>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose}>J'ai noté le password</Button>
        </div>
      </div>
    </Modal>
  );
}
